import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import Snackbar from 'components/DesignSystem/Snackbar';
import Segmented from 'components/DesignSystem/Segmented';
import Graph from 'components/Flux/Automatic/Monthly/Graph';
import Table from 'components/Flux/Automatic/Monthly/Table';
import FullscreenTable from 'components/Layout/MobileShell/FullscreenTable';
import FullControlPanelSheet from 'components/Flux/Automatic/Mobile/FullControlPanelSheet';
import QuerySummary from 'components/Flux/Automatic/Mobile/QuerySummary';
import SavePreferencesModal from 'components/Flux/Automatic/ControlPanel/SavedPreferences/PreferenceSavingModal';
import useViewToggler from 'utils/hooks/flux/automatic/viewToggler';
import useMonthlyData from 'utils/hooks/flux/automatic/useMonthlyData';
import useFluxAutomaticZipDownload from 'utils/hooks/flux/automatic/useFluxAutomaticZipDownload';
import usePastSimulationNames from 'utils/hooks/flux/automatic/usePastSimulationNames';
import useAcomphMLT from 'utils/hooks/flux/automatic/useAcomphMLT';
import { buildSubdivisionParam } from 'helper/flux/automatic';
import {
  selectFluxAutomaticControlPanelMltOption,
  selectFluxAutomaticControlPanelSpatialSubdivision,
  selectFluxAutomaticControlPanelAutomaticStudyScenarios,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';
import { cssVar } from 'constants/theme';

import FullscreenChart from './FullscreenChart';

import './index.scss';

type ViewMode = 'graph' | 'table';

export default function MonthlyMobile() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const { pastSimulationNames } = usePastSimulationNames();
  const unidade = useSelector(selectFluxAutomaticControlPanelMltOption);
  const models = useSelector(selectFluxAutomaticControlPanelAutomaticStudyScenarios);
  const subdivision = useSelector(selectFluxAutomaticControlPanelSpatialSubdivision);
  const {
    showGraph, showTable, toggleGraph, toggleTable,
  } = useViewToggler();
  const { data, graphicReadyData, isFetching } = useMonthlyData();
  const { acomph, mlt } = useAcomphMLT();

  const dataIsEmpty = Object.keys(graphicReadyData).length === 0;
  const spatialSubdivisionParam = useMemo(() => buildSubdivisionParam(subdivision), [subdivision]);
  const { DownloadZipButton, DownloadConfirmationDialog } = useFluxAutomaticZipDownload({
    models,
    unidade,
    type: 'monthly',
    pastSimulationNames,
    spatialSubdivisionParam,
  });

  const extraChips = useMemo(() => {
    const result: string[] = [unidade.toLocaleUpperCase()];
    if (acomph) result.push('ACOMPH');
    if (mlt) result.push('MLT');
    return result;
  }, [unidade, acomph, mlt]);

  // O ViewToggler do desktop são 2 botões de ícone; no mobile o mesmo par vira um
  // Segmented rotulado, que é o padrão das demais telas mobile.
  const viewMode: ViewMode = showTable ? 'table' : 'graph';
  const setViewMode = (mode: ViewMode) => (mode === 'table' ? toggleTable() : toggleGraph());

  return (
    <div className="flux-automatic-monthly-mobile">
      <DownloadConfirmationDialog />
      <SavePreferencesModal />

      <QuerySummary
        onOpenPanel={() => setSheetOpen(true)}
        extraChips={extraChips}
        action={models.length ? <DownloadZipButton /> : undefined}
      />

      {isFetching && <div className="text-center py-4"><Spinner /></div>}

      {!isFetching && dataIsEmpty && (
        <Snackbar>
          Nenhum dado para os filtros selecionados.
        </Snackbar>
      )}

      {!isFetching && !dataIsEmpty && (
        <div className="flux-automatic-monthly-mobile-chart-card">
          <div className="flux-automatic-monthly-mobile-chart-card-header">
            <Segmented<ViewMode>
              size="sm"
              options={[
                { label: 'Gráfico', value: 'graph' },
                { label: 'Tabela', value: 'table' },
              ]}
              value={viewMode}
              onChange={setViewMode}
            />
            {(showGraph || showTable) && (
              <button
                type="button"
                aria-label="Tela cheia"
                className="flux-automatic-monthly-mobile-chart-card-action"
                onClick={() => setFullscreenOpen(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
              </button>
            )}
          </div>

          {showGraph && (
            <div className="flux-automatic-monthly-mobile-graph">
              <Graph data={graphicReadyData} formatString="yyyyMM" height="65vh" />
            </div>
          )}

          {showTable && (
            <div className="table-responsive flux-automatic-monthly-mobile-table">
              <Table data={data} stickyRowLabel />
            </div>
          )}
        </div>
      )}

      <FullControlPanelSheet
        open={sheetOpen}
        dismiss={() => setSheetOpen(false)}
        tab="monthly"
      />
      {showGraph && (
        <FullscreenChart
          open={fullscreenOpen}
          dismiss={() => setFullscreenOpen(false)}
          data={graphicReadyData}
          formatString="yyyyMM"
        />
      )}
      {showTable && (
        <FullscreenTable open={fullscreenOpen} dismiss={() => setFullscreenOpen(false)}>
          <div className="table-responsive flux-automatic-monthly-mobile-table">
            <Table data={data} stickyRowLabel />
          </div>
        </FullscreenTable>
      )}
    </div>
  );
}
